import FARE_VISUALIZATION_TYPES from "../../constants/fareVisualizationTypes";
import {getRoadColorByClass} from "../../constants/routeColors";
import Vector from "./Vector";
import MyPoint from "./MyPoint";

/**
 * MyVectorCollection - класс для работы с коллекциями векторов
 * JavaScript версия MyVectorCollection из VectorGeometryLib
 * Обновлено для соответствия актуальной версии
 */
class MyVectorCollection {
    /**
     * Конструктор коллекции
     */
    constructor() {
        this._vectors = [];
        this._parallelLinesCache = new Map();
    }

    /**
     * Конструктор с начальной емкостью
     * @param {number} capacity - начальная емкость
     */
    static withCapacity(capacity) {
        const collection = new MyVectorCollection();
        collection._vectors = new Array(capacity);
        return collection;
    }

    /**
     * Конструктор из массива точек
     * @param {MyPoint[]} points - массив точек
     * @returns {MyVectorCollection} новая коллекция
     */
    static fromPoints(points) {
        if (!points || points.length < 2) {
            throw new Error("Необходимо минимум 2 точки для создания векторов");
        }

        const collection = new MyVectorCollection();
        for (let i = 0; i < points.length - 1; i++) {
            collection.add(new Vector(points[i], points[i + 1]));
        }
        return collection;
    }

    /**
     * Конструктор из массива точек
     * @param {any[]} segments - массив точек
     * @param platonFares - стоимости платона на участках
     * @param getPlatonFare - функция для расчета стоимости платона
     * @param getPolylineTooltip - функция для генерации тултипа
     * @returns {MyVectorCollection} новая коллекция
     */
    static fromSegments(segments, platonFares, getPlatonFare, getPolylineTooltip) {

        const collection = new MyVectorCollection();
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const fareVisualizationType = segment.fareVisualizationType;
            const hasEmptyRun = (fareVisualizationType & FARE_VISUALIZATION_TYPES.emptyTruck) !== 0;
            const fare = (fareVisualizationType & FARE_VISUALIZATION_TYPES.platon) !== 0 ? getPlatonFare(platonFares, i) : 0;
            const color = getRoadColorByClass(fareVisualizationType);
            const tooltip = getPolylineTooltip(fareVisualizationType, fare);

            for (let j = 0; j < segment.geoPoints.length; j++) {
                const points = segment.geoPoints[j];
                for (let i = 0; i < points.length - 1; i++) {
                    collection.add(Vector.createWithPolylineParams(new MyPoint(points[i].lon, points[i].lat), new MyPoint(points[i + 1].lon, points[i + 1].lat), color, hasEmptyRun, tooltip));
                }
            }
        }
        return collection;
    }

    /**
     * Конструктор из массива точек (Span версия)
     * @param {MyPoint[]} points - массив точек
     * @returns {MyVectorCollection} новая коллекция
     */
    static fromPointsSpan(points) {
        if (points.length < 2) {
            throw new Error("Необходимо минимум 2 точки для создания векторов");
        }

        const collection = new MyVectorCollection();
        for (let i = 0; i < points.length - 1; i++) {
            collection.add(new Vector(points[i], points[i + 1]));
        }
        return collection;
    }

    /**
     * Количество векторов в коллекции
     */
    get Count() {
        return this._vectors.length;
    }

    /**
     * Получить вектор по индексу
     * @param {number} index - индекс вектора
     * @returns {Vector} вектор
     */
    get(index) {
        return this._vectors[index];
    }

    /**
     * Установить вектор по индексу
     * @param {number} index - индекс
     * @param {Vector} vector - вектор
     */
    set(index, vector) {
        this._vectors[index] = vector;
        this.invalidateCache();
    }

    /**
     * Добавить вектор в коллекцию
     * @param {Vector} vector - вектор для добавления
     */
    add(vector) {
        this._vectors.push(vector);
        this.invalidateCache();
    }

    /**
     * Добавить вектор из двух точек
     * @param {MyPoint} start - начальная точка
     * @param {MyPoint} end - конечная точка
     */
    addPoints(start, end) {
        this.add(new Vector(start, end));
    }

    /**
     * Добавить вектор из координат
     * @param {number} startX - X координата начальной точки
     * @param {number} startY - Y координата начальной точки
     * @param {number} endX - X координата конечной точки
     * @param {number} endY - Y координата конечной точки
     */
    addCoordinates(startX, startY, endX, endY) {
        this.add(Vector.fromCoordinates(startX, startY, endX, endY));
    }

    /**
     * Добавить массив точек как последовательность векторов
     * @param {MyPoint[]} points - массив точек
     */
    addPointsArray(points) {
        if (!points || points.length < 2) return;

        for (let i = 0; i < points.length - 1; i++) {
            this.add(new Vector(points[i], points[i + 1]));
        }
    }

    /**
     * Добавить массив точек как последовательность векторов (Span версия)
     * @param {MyPoint[]} points - массив точек
     */
    addPointsSpan(points) {
        if (points.length < 2) return;

        for (let i = 0; i < points.length - 1; i++) {
            this.add(new Vector(points[i], points[i + 1]));
        }
    }

    /**
     * Удалить вектор по индексу
     * @param {number} index - индекс вектора
     */
    removeAt(index) {
        this._vectors.splice(index, 1);
        this.invalidateCache();
    }

    /**
     * Очистить коллекцию
     */
    clear() {
        this._vectors = [];
        this.invalidateCache();
    }

    /**
     * Получить все точки коллекции
     * @returns {MyPoint[]} массив точек
     */
    getPoints() {
        if (this._vectors.length === 0) {
            return [];
        }

        const points = [];
        points.push(this._vectors[0].StartPoint);

        for (let i = 0; i < this._vectors.length; i++) {
            points.push(this._vectors[i].EndPoint);
        }

        return points;
    }

    /**
     * ОБНОВЛЕННЫЙ: Создать параллельные линии с заданным смещением
     * Использует оптимизированный алгоритм пересечения
     * @param {number} offset - смещение
     * @returns {MyPoint[]} массив точек параллельных линий
     */
    createParallelLines(offset) {
        // console.log(`createParallelLines: начало, количество векторов: ${this._vectors.length}, смещение: ${offset}`);
        
        const cacheKey = offset.toString();
        if (this._parallelLinesCache.has(cacheKey)) {
            // console.log('createParallelLines: используем кэшированный результат');
            return [...this._parallelLinesCache.get(cacheKey)];
        }

        if (this._vectors.length === 0) {
            // console.log('createParallelLines: нет векторов');
            return [];
        }

        const parallelPoints = [];
        const minLength = 0.5 * Math.abs(offset);

        // Обрабатываем векторы с объединением коротких
        for (let i = 0; i < this._vectors.length; i++) {
            const currentVector = this._vectors[i];
            let combinedVector = currentVector;
            let nextIndex = i + 1;

            // console.log(`createParallelLines: начинаем с вектора ${i}, длина: ${combinedVector.Length}`);
        
            // Объединяем векторы, пока длина меньше минимальной
            while (combinedVector.Length < minLength && nextIndex < this._vectors.length) {
                const nextVector = this._vectors[nextIndex];
                // Создаем объединенный вектор от начала текущего до конца следующего
                combinedVector = new Vector(combinedVector.StartPoint, nextVector.EndPoint);
                // console.log(`createParallelLines: объединили с вектором ${nextIndex}, новая длина: ${combinedVector.Length}`);
                nextIndex++;
            }

            // Если это первый вектор и он достаточно длинный
            if (i === 0 && combinedVector.Length >= minLength) {
                const firstPerp = combinedVector.perpendicularStart(offset);
        parallelPoints.push(firstPerp.EndPoint);
        // console.log(`createParallelLines: добавлена первая точка: (${firstPerp.EndPoint.x}, ${firstPerp.EndPoint.y})`);
            }

            // Если это не последний вектор, обрабатываем пересечение
            if (nextIndex < this._vectors.length) {
                const nextVector = this._vectors[nextIndex];
                let nextCombinedVector = nextVector;
                let nextNextIndex = nextIndex + 1;

                // Объединяем следующие векторы, если нужно
                while (nextCombinedVector.Length < minLength && nextNextIndex < this._vectors.length) {
                    const nextNextVector = this._vectors[nextNextIndex];
                    nextCombinedVector = new Vector(nextCombinedVector.StartPoint, nextNextVector.EndPoint);
                    // console.log(`createParallelLines: объединили следующий с вектором ${nextNextIndex}, новая длина: ${nextCombinedVector.Length}`);
                    nextNextIndex++;
                }

            // Создаем параллельные векторы
                const currentParallel = combinedVector.parallelVector(offset);
                const nextParallel = nextCombinedVector.parallelVector(offset);

            // Вычисляем пересечение параллельных векторов
            const intersection = currentParallel.cross(nextParallel);
            // console.log(`createParallelLines: пересечение: (${intersection.x}, ${intersection.y})`);
            
            // Если пересечение не найдено, используем среднюю точку
            if (isNaN(intersection.x) || isNaN(intersection.y)) {
                const midPoint = currentParallel.EndPoint.add(nextParallel.StartPoint).divide(2);
                parallelPoints.push(midPoint);
                // console.log(`createParallelLines: используем среднюю точку: (${midPoint.x}, ${midPoint.y})`);
                } else {
                    // Проверяем расстояние от точки пересечения до исходной линии
                    const currentPoint = combinedVector.EndPoint;
                    const distanceToOriginal = intersection.distanceTo(currentPoint);
                    
                    // Если расстояние больше 3 * offset, удаляем эту точку
                    if (distanceToOriginal > 3 * Math.abs(offset)) {
                        const midPoint = currentParallel.EndPoint.add(nextParallel.StartPoint).divide(2);
                        parallelPoints.push(midPoint);
                        // console.log(`createParallelLines: расстояние слишком большое (${distanceToOriginal}), используем среднюю точку: (${midPoint.x}, ${midPoint.y})`);
            } else {
                parallelPoints.push(intersection);
                        // console.log(`createParallelLines: используем пересечение: (${intersection.x}, ${intersection.y}), расстояние: ${distanceToOriginal}`);
                    }
            }
        }

            // Если это последний вектор и он достаточно длинный
            if (nextIndex >= this._vectors.length && combinedVector.Length >= minLength) {
                const lastPerp = combinedVector.perpendicularEnd(offset);
        parallelPoints.push(lastPerp.EndPoint);
        // console.log(`createParallelLines: добавлена последняя точка: (${lastPerp.EndPoint.x}, ${lastPerp.EndPoint.y})`);
            }

            // Пропускаем обработанные векторы
            i = nextIndex - 1;
        }

        // Кэшируем результат
        this._parallelLinesCache.set(cacheKey, [...parallelPoints]);
        // console.log(`createParallelLines: создано ${parallelPoints.length} точек`);

        return parallelPoints;
    }

    /**
     * Создать противоположные параллельные линии
     * @param {number} offset - смещение
     * @returns {MyPoint[]} массив точек противоположных параллельных линий
     */
    createOppositeParallelLines(offset) {
        return this.createParallelLines(-offset);
    }

    /**
     * Создать пару параллельных линий
     * @param {number} offset - смещение
     * @returns {Object} объект с upper и lower массивами точек
     */
    createParallelPair(offset) {
        return {
            upper: this.createParallelLines(offset),
            lower: this.createParallelLines(-offset)
        };
    }

    /**
     * ОБНОВЛЕННЫЙ: Создать смещенную коллекцию векторов (метод Offset для совместимости)
     * @param {number} offset - смещение
     * @returns {MyVectorCollection} новая коллекция
     */
    offset(offset) {
        // console.log(`offset: начало, количество векторов: ${this._vectors.length}, смещение: ${offset}`);
        
        if (this._vectors.length === 0) {
            // console.log('offset: нет векторов, возвращаем пустую коллекцию');
            return new MyVectorCollection();
        }
        
        // Используем CreateParallelLines для правильного вычисления точек соединения
        // console.log('offset: вызываем createParallelLines');
        const parallelPoints = this.createParallelLines(offset);
        // console.log(`offset: createParallelLines вернул ${parallelPoints.length} точек`);
        
        if (parallelPoints.length < 2) {
            // console.log('offset: недостаточно точек для создания векторов');
            return new MyVectorCollection();
        }
        
        // console.log('offset: создаем новую VectorCollection');
        const vc = new MyVectorCollection();
        
        // Создаем векторы из точек, соединяя их последовательно
        for (let i = 0; i < parallelPoints.length - 1; i++) {
            const vector = new Vector(parallelPoints[i], parallelPoints[i + 1]);
            vc.add(vector);
            // console.log(`offset: добавлен вектор ${i}: (${parallelPoints[i].x}, ${parallelPoints[i].y}) -> (${parallelPoints[i + 1].x}, ${parallelPoints[i + 1].y})`);
        }
        
        // console.log(`offset: создана коллекция с ${vc.Count} векторами`);
        return vc;
    }

    /**
     * Вычислить точку пересечения для создания параллельных линий
     * @param {Vector} v1 - первый вектор
     * @param {Vector} v2 - второй вектор
     * @param {number} offset - смещение
     * @returns {MyPoint} точка пересечения
     */
    calculateIntersection(v1, v2, offset) {
        // Создаем параллельные векторы
        const parallel1 = v1.parallelVector(offset);
        const parallel2 = v2.parallelVector(offset);

        // Находим пересечение параллельных линий
        const intersection = parallel1.cross(parallel2);
        if (!isNaN(intersection.x) && !isNaN(intersection.y)) {
            return intersection;
        }

        // Если пересечения нет (параллельные линии), используем среднюю точку
        const midPoint = parallel1.EndPoint.add(parallel2.StartPoint).divide(2);
        return midPoint;
    }

    /**
     * Получить общую длину всех векторов
     * @returns {number} общая длина
     */
    getTotalLength() {
        let totalLength = 0;
        for (const vector of this._vectors) {
            totalLength += vector.Length;
        }
        return totalLength;
    }

    /**
     * Получить площадь многоугольника
     * @returns {number} площадь многоугольника
     */
    getPolygonArea() {
        const points = this.getPoints();
        if (points.length < 3) return 0;

        return Vector.polygonArea(points);
    }

    /**
     * Проверить, является ли многоугольник выпуклым
     * @returns {boolean} true если многоугольник выпуклый
     */
    isConvex() {
        if (this._vectors.length < 3) return true;

        let sign = 0;
        for (let i = 0; i < this._vectors.length; i++) {
            const current = this._vectors[i];
            const next = this._vectors[(i + 1) % this._vectors.length];

            const cross = current.Delta.cross(next.Delta);
            if (Math.abs(cross) > Vector.EPSILON) {
                if (sign === 0) {
                    sign = cross > 0 ? 1 : -1;
                } else if ((cross > 0 && sign < 0) || (cross < 0 && sign > 0)) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Получить центроид многоугольника
     * @returns {MyPoint} центроид
     */
    getCentroid() {
        const points = this.getPoints();
        if (points.length === 0) {
            return MyPoint.ZERO;
        }

        let sumX = 0, sumY = 0;
        for (const point of points) {
            sumX += point.x;
            sumY += point.y;
        }

        return new MyPoint(sumX / points.length, sumY / points.length);
    }

    /**
     * Получить ограничивающий прямоугольник
     * @returns {Object} объект с min и max точками
     */
    getBoundingBox() {
        if (this._vectors.length === 0) {
            return { min: MyPoint.ZERO, max: MyPoint.ZERO };
        }

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const vector of this._vectors) {
            minX = Math.min(minX, vector.StartPoint.x, vector.EndPoint.x);
            minY = Math.min(minY, vector.StartPoint.y, vector.EndPoint.y);
            maxX = Math.max(maxX, vector.StartPoint.x, vector.EndPoint.x);
            maxY = Math.max(maxY, vector.StartPoint.y, vector.EndPoint.y);
        }

        return {
            min: new MyPoint(minX, minY),
            max: new MyPoint(maxX, maxY)
        };
    }

    /**
     * Масштабировать коллекцию
     * @param {number} scaleX - масштаб по X
     * @param {number} scaleY - масштаб по Y
     * @param {MyPoint} center - центр масштабирования
     */
    scale(scaleX, scaleY, center = null) {
        if (!center) {
            center = this.getCentroid();
        }

        for (const vector of this._vectors) {
            vector.startPoint = vector.startPoint.scale(scaleX, scaleY, center);
            vector.endPoint = vector.endPoint.scale(scaleX, scaleY, center);
            vector.invalidateCache();
        }

        this.invalidateCache();
    }

    /**
     * Повернуть коллекцию
     * @param {number} angle - угол в радианах
     * @param {MyPoint} center - центр поворота
     */
    rotate(angle, center = null) {
        if (!center) {
            center = this.getCentroid();
        }

        for (const vector of this._vectors) {
            vector.startPoint = vector.startPoint.rotate(angle, center);
            vector.endPoint = vector.endPoint.rotate(angle, center);
            vector.invalidateCache();
        }

        this.invalidateCache();
    }

    /**
     * Перенести коллекцию
     * @param {MyPoint} offset - вектор переноса
     */
    translate(offset) {
        for (const vector of this._vectors) {
            vector.startPoint = vector.startPoint.add(offset);
            vector.endPoint = vector.endPoint.add(offset);
        }

        this.invalidateCache();
    }

    /**
     * Инвалидировать кэш
     */
    invalidateCache() {
        this._parallelLinesCache.clear();
    }

    /**
     * Получить итератор по векторам
     * @returns {Iterator} итератор
     */
    [Symbol.iterator]() {
        return this._vectors[Symbol.iterator]();
    }

    /**
     * Строковое представление коллекции
     * @returns {string} строка с информацией о коллекции
     */
    toString() {
        return `MyVectorCollection: ${this._vectors.length} vectors, Total Length: ${this.getTotalLength().toFixed(2)}`;
    }

    /**
     * Создать копию коллекции
     * @returns {MyVectorCollection} новая коллекция
     */
    clone() {
        const collection = new MyVectorCollection();
        for (const vector of this._vectors) {
            collection.add(new Vector(vector.StartPoint.clone(), vector.EndPoint.clone()));
        }
        return collection;
    }
}

// Для обратной совместимости
class VectorCollection extends MyVectorCollection {
    constructor() {
        super();
    }
}

export default VectorCollection;